// Entitlement helpers — single place to interpret the `entitlements` array the
// .NET auth response returns (active product lines, one per Subscription).
//
// Portal access rules (URL decides the portal, entitlements decide access):
//  - /app (reader):   everyone EXCEPT API-only accounts. Pre-backfill legacy
//    users have entitlements === [] and must keep full reader access.
//  - /api (dev API):  requires the DeveloperApi entitlement.
// An account holding BOTH entitlements can use both portals.

export const READER_APP = 'ReaderApp';
export const DEVELOPER_API = 'DeveloperApi';

const entitlementsOf = (user) => (user && user.entitlements) || [];

export const hasDeveloperApi = (user) =>
  entitlementsOf(user).includes(DEVELOPER_API);

export const hasReaderApp = (user) => entitlementsOf(user).includes(READER_APP);

// True only for a genuinely authenticated session. IMPORTANT: on logout the user reducer sets
// state.user to the truthy string 'logged out' (not null), so a bare `if (user)` / `!user` is NOT a
// valid "logged in" check — it treats a logged-out session as authenticated. Gate on the token.
export const isAuthenticated = (user) => Boolean(user && user.token);

// API-only = holds DeveloperApi and NOT ReaderApp. Only these accounts are
// bounced out of the reader portal / skipped by reader-content population.
export const isApiOnly = (user) => hasDeveloperApi(user) && !hasReaderApp(user);

// Show the portal switcher only when the account can use both portals.
export const hasBothPortals = (user) =>
  hasDeveloperApi(user) && hasReaderApp(user);

// API keys are granted by any keyed DeveloperApi plan. Note: since the Trial plan became
// self-serve (customers create their own key), this is true for BOTH Trial and Production — so
// it must NOT be used to tell the two apart. Use `isApiProduction` for that.
export const canUseApiKeys = (user) =>
  ((user && user.subscriptions) || []).some(
    (s) => s.productLine === DEVELOPER_API && s.allowsApiKeys
  );

// The plan name on the DeveloperApi subscription (e.g. "API Trial" / "API Production"), '' if none.
export const apiPlanName = (user) =>
  (
    ((user && user.subscriptions) || []).find(
      (s) => s.productLine === DEVELOPER_API
    ) || {}
  ).planName || '';

// Production = the DeveloperApi plan is a Production plan (uncapped, sales-provisioned). Trial and
// Production both grant keys, so the plan NAME is the real signal — mirrors the Overview's check.
export const isApiProduction = (user) =>
  apiPlanName(user).toLowerCase().includes('production');

// Customer-facing plan label. The backend plan is named "API Production" (kept for detection and
// admin provisioning), but customers see the paid tier as "Enterprise". Trial stays "Trial".
export const displayApiPlan = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('production')) return 'Enterprise';
  if (n.includes('trial')) return 'Trial';
  return name || '';
};

// ── Reader-app entitlement helpers ─────────────────────────────────────────
// The reader app follows the same two-state model as the API: a self-serve,
// time-boxed Trial, and a sales-provisioned Enterprise tier (no self-serve
// pricing). These read the ReaderApp Subscription the auth response returns.

const readerSub = (user) =>
  ((user && user.subscriptions) || []).find((s) => s.productLine === READER_APP);

// The plan name on the ReaderApp subscription (e.g. "Enterprise"/"Free"), '' if none.
export const readerPlanName = (user) => (readerSub(user) || {}).planName || '';

// Enterprise = the reader plan is the sales-provisioned Enterprise tier.
export const isReaderEnterprise = (user) =>
  readerPlanName(user).toLowerCase().includes('enterprise');

// Trial = the reader subscription is in a (time-boxed) trial state.
export const isReaderTrial = (user) =>
  ((readerSub(user) || {}).status || '').toLowerCase() === 'trialing';

// When the reader trial lapses (ISO string), or null when not on a trial.
export const readerTrialEndsAt = (user) => {
  const s = readerSub(user);
  return s && (s.status || '').toLowerCase() === 'trialing' ? s.endDate : null;
};

// Whole days left on the reader trial (0 if lapsed/none).
export const readerTrialDaysLeft = (user) => {
  const ends = readerTrialEndsAt(user);
  if (!ends) return 0;
  const ms = new Date(ends).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

// Seats entitled on the reader Enterprise subscription (null when not seat-limited).
export const readerSeats = (user) => {
  const s = (readerSub(user) || {}).seats;
  return typeof s === 'number' && s > 0 ? s : null;
};

// Contract renewal (end) date for an Active reader subscription (ISO string), else null.
// Recorded for reference — access stays active regardless (billing is off-platform).
export const readerRenewalDate = (user) => {
  const s = readerSub(user);
  return s && (s.status || '').toLowerCase() === 'active' && s.endDate
    ? s.endDate
    : null;
};

// True when the account had reader access that has now lapsed (e.g. an expired trial): it holds a
// reader subscription but no longer has the ReaderApp entitlement. Drives the "trial ended" state.
export const readerAccessLapsed = (user) =>
  Boolean(user && user.hasReaderSubscription) && !hasReaderApp(user);

// Negotiated price + interval on the reader subscription, or null when none recorded.
export const readerNegotiatedPrice = (user) => {
  const s = readerSub(user) || {};
  return s.priceOverride != null
    ? { amount: s.priceOverride, interval: s.priceInterval }
    : null;
};

// SuperAdmin gate for the internal admin area (/api/admin). The .NET auth response returns the
// user's roles; SuperAdmin is a seeded role (also enforced server-side on the admin endpoints —
// this is UX gating only).
export const isSuperAdmin = (user) =>
  ((user && user.roles) || []).some(
    (r) => (r || '').toLowerCase() === 'superadmin'
  );

// The customer's own org admin (seeded/promoted at Enterprise provisioning). Gates the Team page.
// Gate on the real role — the reader login payload hard-codes isAdmin:true, so it can't be trusted.
export const isCorporateAdmin = (user) =>
  ((user && user.roles) || []).some(
    (r) => (r || '').toLowerCase() === 'corporateadmin'
  );
